"use client";

import { useCallback, useRef, useState } from "react";
import { Button } from "@/app/components/ui/button";
import { Copy, Check } from "lucide-react";
import { cn } from "@/app/lib/utils";

interface CopyButtonProps {
  text: string;
  className?: string;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  size?: "default" | "sm" | "lg" | "icon";
  children?: React.ReactNode;
  onCopy?: () => void;
  successMessage?: string;
  showTextOnCopy?: boolean;
  /** Accessible label when no visible children are provided (icon-only) */
  label?: string;
}

const RESET_DELAY_MS = 2000;

export function CopyButton({
  text,
  className,
  variant = "outline",
  size = "sm",
  children,
  onCopy,
  successMessage = "Copied!",
  showTextOnCopy = true,
  label = "Copy to clipboard",
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    if (copied) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.();

      // Clear any existing timeout to prevent stale resets
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), RESET_DELAY_MS);
    } catch {
      // Fallback for older browsers / insecure contexts
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);

      setCopied(true);
      onCopy?.();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), RESET_DELAY_MS);
    }
  }, [copied, text, onCopy]);

  const isIconOnly = !children && !showTextOnCopy;

  return (
    <Button
      type="button"
      onClick={handleCopy}
      variant={variant}
      size={size}
      aria-label={isIconOnly ? (copied ? successMessage : label) : undefined}
      aria-live="polite"
      className={cn(
        "inline-flex items-center gap-1.5 transition-colors duration-200",
        copied && "bg-green-600 hover:bg-green-700 text-white border-green-600",
        className,
      )}
    >
      <span
        className="inline-flex shrink-0 items-center justify-center size-4"
        aria-hidden="true"
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </span>

      {copied
        ? showTextOnCopy && (
            <span>{children ?? successMessage}</span>
          )
        : children && <span>{children}</span>}
    </Button>
  );
}